import * as THREE from 'three';
import { FishBatch, facesRight, fishAtlas, type FishDraw } from '../fishBatch';
import { pickNearest } from '../pick';
import { hash3 } from '../fishSwim';
import { OCTOPUS_SPRITES } from '../../ui/data/octopusSprites';
import type { Sprite } from '../../ui/data/spriteTypes';
import type { ReefOctopus } from './data';
import type { FishPose, PosableFish } from '../fishSchools';

/** The day octopus's seven colors, the user's pictures' (`scripts/octopusArt.ts`), in their order. */
export type OctopusColor = keyof typeof OCTOPUS_SPRITES;
export const OCTOPUS_COLORS = Object.keys(OCTOPUS_SPRITES) as OctopusColor[];

/**
 * The day octopus on Maui Nui Reef (M158; the user's word: "all 7 octopus variants. when they change colors, make them
 * fade into the other color over 2-3 seconds"), from three works the user handed over: Forsythe and Hanlon 1997 (*J.
 * Exp. Mar. Biol. Ecol.* 209: 15-31; two octopuses followed by divers from first light to dusk for six days at Manihi
 * Atoll), Hanlon, Forsythe and Joneschild 1999 (*Biol. J. Linn. Soc.* 66: 1-22; 7.5 hours of their foraging filmed) and
 * Ivey 2007 (M.S. thesis, University of Hawaiʻi; eight tagged by acoustic transmitters in Kāneʻohe Bay, Oʻahu, nine
 * active periods of three tracked); Hawaiʻi's figures where Ivey gives them:
 *
 * - **Its den and its forages**: "The octopuses stay hidden in dens for the vast majority of time, emerging to forage
 *   early in the morning (usually at first light, well in advance of sunrise), then return to their dens before midday,
 *   and forage once again in late afternoon" (Forsythe and Hanlon, p. 28), "usually between 0530-0830, and again
 *   between 1530-1830" (p. 20); in Hawaiʻi "all except the longest ... took place during the first or last 3 hours of
 *   light" (Ivey, p. 72): `activeH`, each octopus's morning forage within the first 3 hours of light and its afternoon's
 *   within the last, at an hour its own each day, and none at midday or by night, the light from first light to last
 *   (civil twilight's, the sun 6° under the horizon, `civilDuskHour`: M201, the user's word: "if i set to dusk or dawn,
 *   you cant really see it", none was out at sunrise or sunset when the light was counted from sunrise). Each `forageMin`, Hawaiʻi's mean, "The
 *   average duration in this study was 112 min" (Ivey, p. 73), measured (30 to 314 min; Manihi's 118, n = 16, Forsythe
 *   and Hanlon's abstract): two a day, 31% of a 12-hour day, Manihi's "about 28% of daylight hours foraging".
 * - **How it moves**: "a saltatory (stop and go) search strategy" (Forsythe and Hanlon, abstract), moving 21% of its time
 *   out (`moveShare`, measured: "On average, 21% of the time away from the den was spent moving", p. 21; the rest
 *   groping, pouncing and sitting), in moves of `spellS` (JUDGED) between stops as long as that share gives; its pace
 *   moving Hawaiʻi's mean speed over its forage, "mean=0.73 m/min" (Ivey, p. 42, measured, path over duration) over its
 *   moving share; toward a point of its own within `rangeM` of its den and on to another once there, the reach of a
 *   forage, "the maximum distance an octopus moved from the track's origin during an active period ... averaged 13.6±3.3
 *   m (range 2.9-35; N=9)" (Ivey, p. 42, measured).
 * - **Its colors**: "On average, octopuses changed their phenotype 2.95 times/minute, or 177 times per hour, based upon
 *   7.5 hours of videotaped foraging" (Hanlon et al., p. 1): `changesPerMin`, measured (Manihi's 2.57, Palau's dead
 *   coral's 4.13 and its live coral's 4.56, p. 16). Its patterns "chronic (minutes or hours) or acute (seconds or a few
 *   minutes)" (p. 11): the user's `pale` (its Blanching White Web, the pounce's, the web blanching "momentarily",
 *   Forsythe and Hanlon, p. 18) and `passingCloud` (its Passing Cloud; each cloud's "duration of these displays averaged
 *   0.85 s", Mather and Mather 2004, *J. Zool.* 263: 89-94, p. 91, made "during active hunting", p. 92) acute, held no longer
 *   than their fade in and turned back to a chronic one; the other five chronic, held an even chance's wait at least as
 *   long as the fade, so the mean wait over the changes is the paper's 60 / `changesPerMin` (`CHRONIC_WAIT_S`). Each
 *   change from a chronic one to any of the other six as likely (JUDGED: the paper gives no share of time in each; its
 *   "most common body pattern ... was the Reticulated Mottle", p. 13, white markings on a darker mottle, nearest the
 *   user's `whiteSpots`), fading over `fadeS` (the user's "2-3 seconds"; the paper's are swifter, "about 11 swift body
 *   pattern and colour changes" in 16 seconds, Fig. 2).
 * - The user's seven as the paper names its patterns (Table 2, p. 12): `mottled` its Mottle; `pale` its Blanching White
 *   Web; `dark` its All Dark; `green` its Rugose General Resemblance ("clumps of algae", p. 12); `passingCloud` its
 *   Passing Cloud; `whiteSpots` its Reticulated Mottle; `red` none of its names, Hawaiʻi's "most often a warm ochreous
 *   red suffused and blotched with purple" (Kramer 1986, NOAA TR NMFS 38, p. 74, compiled from Robson 1929).
 *
 * Each a rendering choice besides (JUDGED): `fadeInS` its fading in and out at its den, as the visitors' (`VISITOR_DRAW`);
 * `leastLight` the least light it is drawn by, so a camouflaged animal at the Dawn and Dusk hours is seen and can be
 * followed (M201, the user's report: "if i set to dusk or dawn, you cant really see it"): the scene's animals' light at
 * Maui Nui Reef's middle depth, 15 m, on a new game's first day is 0.223 at sunrise and sunset (the two presets' hours)
 * and 0.798 half an hour after sunrise (MEASURED, the water's `seen`), so it is drawn at least as by that half hour's;
 * `denMarkM` how high over its den a tagged octopus's name stands while it is inside (its mantle's height and some).
 */
export const OCTOPUS_DRAW = {
  changesPerMin: 2.95,
  fadeS: 2.5,
  activeH: 3,
  forageMin: 112,
  moveShare: 0.21,
  speedMPerMin: 0.73,
  spellS: 4,
  rangeM: 13.6,
  fadeInS: 2,
  riseS: 1,
  leastLight: 0.8,
  denMarkM: 0.3,
} as const;

/**
 * Its stalking and its fright (M265, the interactions brief's section 7), JUDGED timings over the behaviors the sources
 * above give: of its moves, `stalkOdds` end in a pounce, "groping, pouncing" among what it does out (Forsythe and Hanlon,
 * p. 21): it creeps the first `creepShare` of the move at `creepPace` of its pace and covers the rest in a rush, so the
 * move's length is its pace's as ever (Ivey's mean speed kept), its web spread `spread` wider at the rush's height (its
 * pattern left to its own changes, so their measured 2.95 a minute hold: a pale one forced at each pounce had added
 * changes, the first draft's); no prey is drawn or taken (the simulation gives the octopus none here). Startled by a predator near (the ulua, its predator in the simulation, or a large visitor) within
 * `startleLengths` of the threat's length, at least `startleM`, it blanches (`pale`) and goes back to its den at
 * `retreatTimes` its pace, stays in it `hideS`, and comes out again while its forage lasts.
 */
export const OCTOPUS_LIFE = { stalkOdds: 0.3, creepShare: 0.85, creepPace: 0.6, spread: 0.12, startleM: 2.5, startleLengths: 1.5, retreatTimes: 4, hideS: [10, 20] } as const;

/** The acute patterns, shown no longer than their fade (above). */
export const ACUTE: ReadonlySet<OctopusColor> = new Set<OctopusColor>(['pale', 'passingCloud']);

/**
 * A chronic pattern's mean wait, s, so the mean over the changes is 60 / `changesPerMin`: from a chronic one the next is
 * acute at the acute's share of the other six, and from an acute one always chronic, so of the changes a share p / (1 +
 * p) land on an acute one (held `fadeS`) and the rest on a chronic one.
 */
const ACUTE_ODDS = ACUTE.size / (OCTOPUS_COLORS.length - 1);
const ACUTE_SHARE = ACUTE_ODDS / (1 + ACUTE_ODDS);
const CHRONIC_WAIT_S = (60 / OCTOPUS_DRAW.changesPerMin - ACUTE_SHARE * OCTOPUS_DRAW.fadeS) / (1 - ACUTE_SHARE);

/**
 * Its mantle's length, m, at a weight, kg: as the cube root of its weight (an animal the same shape), its coefficient
 * fitted (their logs' mean) to the four Hawaiian octopuses Ivey weighed and measured both (Table 2, p. 92: 3,000 g and
 * 23.0 cm, 3,000 g and 20.0 cm, 2,200 g and 15.0 cm, 1,500 g and 11.5 cm; mantle length "distance from midpoint of eyes
 * to posterior mantle", p. 12), measured: 12.65 cm at a kilogram. Its sprite `SPRITE_PER_MANTLE` times that across, as the
 * user's picture draws it (MEASURED on art/octopus/mottled.png: its eyes' yellow at x 517 on average, its mantle's tip at
 * 1,243, its drawing from 12 to 1,243: 1,232 across for 726 of mantle). Drawn `drawnTimes` that, as the reef's fish are
 * drawn their length (`OctopusReef`).
 */
const IVEY_WEIGHED: readonly (readonly [number, number])[] = [[3000, 23.0], [3000, 20.0], [2200, 15.0], [1500, 11.5]];
const MANTLE_CM_PER_CBRT_G = Math.exp(IVEY_WEIGHED.reduce((a, [g, cm]) => a + Math.log(cm / Math.cbrt(g)), 0) / IVEY_WEIGHED.length);
const SPRITE_PER_MANTLE = (1243 - 12 + 1) / (1243 - 517);
export const mantleM = (weightKg: number): number => (MANTLE_CM_PER_CBRT_G * Math.cbrt(Math.max(0, weightKg) * 1000)) / 100;
export const octopusWidthM = (weightKg: number): number => mantleM(weightKg) * SPRITE_PER_MANTLE;

/**
 * Octopus `key`'s two forages on day `epochDay`, their start and end hours (local solar): each `forageMin` long, the
 * morning's starting within the first `activeH` of light so it ends within it, the afternoon's ending within the last
 * (each at an hour its own, even over what fits), the light `lightHours` long about noon, first light to last.
 */
export function foragesOn(key: number, epochDay: number, lightHours: number): [number, number][] {
  const d = OCTOPUS_DRAW;
  const first = 12 - lightHours / 2;
  const [long, window] = [d.forageMin / 60, Math.min(d.activeH, lightHours / 2)];
  const slack = Math.max(0, window - long);
  const morning = first + slack * hash3(key, epochDay, 1_580);
  const evening = first + lightHours - window + slack * hash3(key, epochDay, 1_589);
  return [[morning, morning + Math.min(long, window)], [evening, evening + Math.min(long, window)]];
}

/** Whether octopus `key` is out of its den at `hour` (local solar) on day `epochDay`, its light `lightHours` long: within one of its two forages. */
export function outAt(key: number, epochDay: number, hour: number, lightHours: number): boolean {
  return foragesOn(key, epochDay, lightHours).some(([a, b]) => hour >= a && hour < b);
}

/** The seconds to its next change of color, from `color`: an acute one's its fade; a chronic one's its fade and an even chance's wait (exponential), its mean `CHRONIC_WAIT_S`. */
export function changeWaitS(key: number, change: number, color: OctopusColor): number {
  if (ACUTE.has(color)) return OCTOPUS_DRAW.fadeS;
  return OCTOPUS_DRAW.fadeS - (CHRONIC_WAIT_S - OCTOPUS_DRAW.fadeS) * Math.log(1 - hash3(key, change, 1_581));
}

/** The color after `from` at change `change`: from a chronic one any of the other six, as likely; from an acute one any chronic one. */
export function nextColor(key: number, change: number, from: OctopusColor): OctopusColor {
  const others = OCTOPUS_COLORS.filter((c) => c !== from && !(ACUTE.has(from) && ACUTE.has(c)));
  return others[Math.floor(hash3(key, change, 1_582) * others.length)] as OctopusColor;
}

/** The reef as the octopuses crawl it: each island's den sites, the top of what is solid under a point, and how many times its size the reef draws its animals (its fish's, so an octopus is seen beside them as it is). */
export interface OctopusReef {
  densOf(island: number): readonly [number, number][];
  groundAt(x: number, z: number): number;
  drawnTimes: number;
  /** The box every creature is kept in, x and z (M127, the user's word: "make sure all the sea creatures locations are within that"). */
  box: { min: readonly [number, number, number]; max: readonly [number, number, number] };
}

/** An octopus as drawn. */
interface Shown {
  octopus: ReefOctopus;
  den: THREE.Vector3;
  at: THREE.Vector3;
  goal: THREE.Vector3;
  wanted: boolean;
  alpha: number;
  /** Its color and the one it fades to, the fade's share done, the seconds to its next change, and the changes so far. */
  from: OctopusColor;
  to: OctopusColor;
  fade: number;
  wait: number;
  changes: number;
  /** Its spells of moving and stopping: the seconds into this one, and how many so far; and the goals it has had. */
  spell: number;
  spells: number;
  goals: number;
  way: { x: number; z: number };
  right: boolean;
  drawn: { middle: THREE.Vector3; width: number; height: number } | null;
  /** Its pounce now, 0 to 1 at its height (M265); and its fright: seconds hidden in its den so far, and how long it stays. */
  pounce?: number;
  startled?: { hidden: number; hide: number; by: string } | null;
}

/** A spell's seconds: a move's `spellS`, a stop's as long as `moveShare` leaves. */
const spellOf = (spells: number): number => (spells % 2 === 0 ? OCTOPUS_DRAW.spellS : (OCTOPUS_DRAW.spellS * (1 - OCTOPUS_DRAW.moveShare)) / OCTOPUS_DRAW.moveShare);

/** Its next goal: a point of its own within `rangeM` of its den, even over the disc, kept in `box` (M158's review: at the reach Ivey measured, one 11.6 m past the box the camera is held to). */
function goalOf(s: Shown, box: OctopusReef['box']): void {
  s.goals++;
  const [r, a] = [OCTOPUS_DRAW.rangeM * Math.sqrt(hash3(s.octopus.key, s.goals, 1_585)), 2 * Math.PI * hash3(s.octopus.key, s.goals, 1_586)];
  s.goal.set(THREE.MathUtils.clamp(s.den.x + r * Math.cos(a), box.min[0], box.max[0]), 0, THREE.MathUtils.clamp(s.den.z + r * Math.sin(a), box.min[2], box.max[2]));
}

/** An octopus as drawn, for a tap and its card. */
export interface OctopusDrawn {
  key: number;
  octopus: ReefOctopus;
  middle: THREE.Vector3;
  width: number;
  height: number;
  alpha: number;
  color: OctopusColor;
  fadingTo: OctopusColor | null;
  /** The hour its forage ends, back in its den (local solar). */
  backAt: number;
}

/** The day's octopuses: each out of its den by its own day, crawling stop and go about it, its colors fading one into the next. */
export class ReefOctopuses {
  readonly group = new THREE.Group();
  private readonly batch: FishBatch;
  private readonly rects = new Map<OctopusColor, [number, number, number, number]>();
  private day: readonly ReefOctopus[] = [];
  private epochDay = 0;
  private hour = 0;
  private lightHours = 12;
  /** Each by its own draw (`ReefOctopus.key`), so a day's new count moves none of the others (M158's review). */
  private readonly shown = new Map<number, Shown>();
  /** Each one's den this day, by its key: its island's sites shared out one to an octopus where there are enough. */
  private dens = new Map<number, THREE.Vector3 | null>();
  private camera: THREE.Camera | null = null;
  /** The octopuses a hunt draws for now, by name (`octo:` and its key; M252: the ulua's hunt on it, `hunts.ts`). */
  private readonly poses = new Map<string, FishPose>();
  private lastPosable: PosableFish[] = [];

  constructor(private readonly reef: OctopusReef) {
    const sprites = OCTOPUS_COLORS.map((c) => OCTOPUS_SPRITES[c] as Sprite);
    const { texture, rects } = fishAtlas(sprites);
    OCTOPUS_COLORS.forEach((c, i) => this.rects.set(c, rects.get(sprites[i] as Sprite) as [number, number, number, number]));
    this.batch = new FishBatch(texture, true);
    // Drawn after the reef and before the fish (M158's reviews: after the fish, the batch its own mesh and no sprite
    // writing depth, an octopus covered every fish swimming in front of it; before the reef's outcrops (3) and its
    // colonies and rubble (0), drawn in the same transparent pass and writing depth, a rock behind it painted over it).
    // A fish behind it now covers it, M157's open item, each batch drawn whole in its turn.
    this.batch.mesh.renderOrder = 3.5;
    this.group.add(this.batch.mesh);
  }

  /** The day's octopuses (`mauiNuiReefData`'s) on day `epochDay`, its light `lightHours` long, first light to last. */
  set(octopuses: readonly ReefOctopus[], epochDay: number, lightHours = 12): void {
    if (epochDay !== this.epochDay) for (const s of this.shown.values()) s.wanted = false;
    [this.day, this.epochDay, this.lightHours] = [octopuses, epochDay, lightHours];
    this.dens = this.densFor(octopuses);
    this.refresh();
  }

  /** The hour: who is out. */
  setHour(hour: number): void {
    if (hour === this.hour) return;
    this.hour = hour;
    this.refresh();
  }

  /**
   * Each one's den: one of its island's sites by its own draw, the next free one where another has it (one to a den,
   * Sims 1998's count; M158's review: a single block's sites shared about one time in nine); none where its island has
   * none.
   */
  private densFor(octopuses: readonly ReefOctopus[]): Map<number, THREE.Vector3 | null> {
    const taken = new Map<number, Set<number>>();
    return new Map(octopuses.map((o) => {
      const dens = this.reef.densOf(o.island);
      const used = taken.get(o.island) ?? new Set<number>();
      taken.set(o.island, used);
      if (dens.length === 0) return [o.key, null];
      let k = Math.floor(hash3(o.key, 0, 1_583) * dens.length);
      for (let tried = 0; used.has(k) && tried < dens.length; tried++) k = (k + 1) % dens.length;
      used.add(k);
      const d = dens[k] as [number, number];
      return [o.key, new THREE.Vector3(d[0], this.reef.groundAt(d[0], d[1]), d[1])];
    }));
  }

  private refresh(): void {
    for (const s of this.shown.values()) s.wanted = false;
    for (const o of this.day) {
      if (!outAt(o.key, this.epochDay, this.hour, this.lightHours)) continue;
      const was = this.shown.get(o.key);
      if (was) {
        Object.assign(was, { octopus: o, wanted: true });
        continue;
      }
      const den = this.dens.get(o.key)?.clone();
      if (!den) continue;
      const first = OCTOPUS_COLORS[Math.floor(hash3(o.key, this.epochDay, 1_584) * OCTOPUS_COLORS.length)] as OctopusColor;
      const s: Shown = { octopus: o, den, at: den.clone(), goal: den.clone(), wanted: true, alpha: 0, from: first, to: first, fade: 1, wait: changeWaitS(o.key, 0, first), changes: 0, spell: 0, spells: 0, goals: 0, way: { x: 1, z: 0 }, right: hash3(o.key, 1, 1_583) < 0.5, drawn: null };
      goalOf(s, this.reef.box);
      this.shown.set(o.key, s);
    }
  }

  /** Its pace moving, m/s: Hawaiʻi's mean speed over a forage over the share of it spent moving. */
  private paceMPerS(): number {
    return OCTOPUS_DRAW.speedMPerMin / 60 / OCTOPUS_DRAW.moveShare;
  }

  /** Moved on by `seconds`: each faded toward out or in its den, its colors toward the next, crawling unless `calm`. */
  swim(seconds: number, calm = false): void {
    const d = OCTOPUS_DRAW;
    for (const [i, s] of this.shown) {
      // Hidden in its den after a fright (M265), as if gone in for the day.
      const inDen = !!s.startled && Math.hypot(s.at.x - s.den.x, s.at.z - s.den.z) < 0.1;
      s.alpha = s.wanted && !inDen ? Math.min(1, s.alpha + seconds / d.fadeInS) : s.alpha - seconds / d.fadeInS;
      if (inDen && s.startled) {
        s.startled.hidden += seconds;
        if (s.startled.hidden >= s.startled.hide) s.startled = null;
        s.alpha = Math.max(0, s.alpha);
      }
      if (s.alpha <= 0 && !s.wanted) {
        this.shown.delete(i);
        continue;
      }
      // Its colors: the fade on, and the next change begun once its wait is out.
      s.fade = Math.min(1, s.fade + seconds / d.fadeS);
      s.wait -= seconds;
      while (s.wait <= 0) {
        s.changes++;
        // Its fade begun when the wait ran out, within this step.
        [s.from, s.to, s.fade] = [s.to, nextColor(s.octopus.key, s.changes, s.to), Math.min(1, -s.wait / d.fadeS)];
        s.wait += changeWaitS(s.octopus.key, s.changes, s.to);
      }
      if (calm) continue;
      s.pounce = 0;
      // Startled: straight back to its den at `retreatTimes` its pace (M265).
      if (s.startled) {
        const [dx, dz] = [s.den.x - s.at.x, s.den.z - s.at.z];
        const far = Math.hypot(dx, dz);
        const step = Math.min(far, this.paceMPerS() * OCTOPUS_LIFE.retreatTimes * seconds);
        if (far > 1e-9) {
          s.at.x += (dx / far) * step;
          s.at.z += (dz / far) * step;
          s.way = { x: dx, z: dz };
        }
        s.at.y += (this.reef.groundAt(s.at.x, s.at.z) - s.at.y) * Math.min(1, seconds / d.riseS);
        continue;
      }
      // Stop and go: an even spell moving toward its goal, a point of its own about its den, `spellS` long; an odd one
      // still, as long as its moving share leaves; a goal reached, the next (M158's sabotage: a new goal each spell,
      // short of the last, kept it jittering by its den).
      s.spell += seconds;
      for (let long = spellOf(s.spells); s.spell >= long; long = spellOf(s.spells)) {
        s.spell -= long;
        s.spells++;
      }
      if (s.spells % 2 === 0) {
        const [dx, dz] = [s.goal.x - s.at.x, s.goal.z - s.at.z];
        const far = Math.hypot(dx, dz);
        // A stalking move (M265): a creep, then the rest of the move's length in a rush, its pounce.
        const L = OCTOPUS_LIFE;
        let pace = 1;
        if (hash3(s.octopus.key, s.spells, 2_670) < L.stalkOdds) {
          const u = s.spell / spellOf(s.spells);
          if (u < L.creepShare) pace = L.creepPace;
          else {
            pace = (1 - L.creepPace * L.creepShare) / (1 - L.creepShare);
            s.pounce = Math.sin((Math.PI * (u - L.creepShare)) / (1 - L.creepShare));
          }
        }
        const step = Math.min(far, this.paceMPerS() * pace * seconds);
        if (far <= step) goalOf(s, this.reef.box);
        if (far > 1e-9) {
          s.at.x += (dx / far) * step;
          s.at.z += (dz / far) * step;
          s.way = { x: dx, z: dz };
        }
      }
      const want = this.reef.groundAt(s.at.x, s.at.z);
      s.at.y += (want - s.at.y) * Math.min(1, seconds / d.riseS);
    }
    this.draw();
  }

  /** Its pattern turned to `color` now (M265: the pounce's, the fright's), faded as every change is, its next change from it. */
  private turnTo(s: Shown, color: OctopusColor): void {
    s.changes++;
    [s.from, s.to, s.fade] = [s.fade < 0.5 ? s.from : s.to, color, 0];
    s.wait = changeWaitS(s.octopus.key, s.changes, color);
  }

  /**
   * The predators near now (M265, `ReefLife`): each octopus out within `OCTOPUS_LIFE.startleLengths` of one's length (at
   * least `startleM`) startled, blanching and going back to its den.
   */
  startle(threats: readonly { species: string; at: THREE.Vector3; width: number }[]): void {
    const L = OCTOPUS_LIFE;
    for (const [key, s] of this.shown) {
      if (s.startled || !s.wanted || s.alpha < 0.5) continue;
      const by = threats.find((t) => Math.hypot(t.at.x - s.at.x, t.at.z - s.at.z) < Math.max(L.startleM, L.startleLengths * t.width));
      if (!by) continue;
      s.startled = { hidden: 0, hide: L.hideS[0] + (L.hideS[1] - L.hideS[0]) * hash3(key, s.changes, 2_671), by: by.species };
      this.turnTo(s, 'pale');
    }
  }

  /** The developer's (the interactions brief's section 13): the nearest octopus out startled back to its den now; whether one was. */
  startleNearest(point: THREE.Vector3): boolean {
    const s = [...this.shown.values()].filter((o) => o.wanted && !o.startled && o.alpha > 0).sort((a, b) => a.at.distanceTo(point) - b.at.distanceTo(point))[0];
    if (!s) return false;
    this.startle([{ species: 'developer', at: s.at.clone(), width: 1 }]);
    return true;
  }

  /** The octopuses' own doings now, for the developer's overlay: each one's key, what it does and why. */
  debug(): { key: number; doing: 'retreat' | 'hidden' | 'pounce' | 'forage'; threat: string | null; at: readonly [number, number, number] }[] {
    return [...this.shown].filter(([, s]) => s.wanted).map(([key, s]) => {
      const hidden = !!s.startled && Math.hypot(s.at.x - s.den.x, s.at.z - s.den.z) < 0.1;
      return { key, doing: hidden ? 'hidden' : s.startled ? 'retreat' : (s.pounce ?? 0) > 0 ? 'pounce' : 'forage', threat: s.startled?.by ?? null, at: [s.at.x, s.at.y, s.at.z] };
    });
  }

  /** Each turned the way `camera` sees it crawl. */
  face(camera: THREE.Camera): void {
    this.camera = camera;
    this.draw();
  }

  private draw(): void {
    const draws: FishDraw[] = [];
    const posable: PosableFish[] = [];
    for (const [key, s] of this.shown) {
      s.right = facesRight(this.camera, s.at, s.way, s.right);
      const sprite = OCTOPUS_SPRITES[s.to] as Sprite;
      // Its web spread at its pounce's height (M265).
      const width = octopusWidthM(s.octopus.weightKg) * this.reef.drawnTimes * (1 + OCTOPUS_LIFE.spread * (s.pounce ?? 0));
      const height = (width * sprite.height) / sprite.width;
      s.drawn = { middle: new THREE.Vector3(s.at.x, s.at.y + height / 2, s.at.z), width, height };
      const own = Math.max(0, s.alpha);
      // As a hunt draws it where one has it (M252), as the fish are drawn (`FishSchools.fit`): its length the drawn
      // width's own, as the hunt weighs a prey against its predator.
      const id = `octo:${key}`;
      posable.push({ id, species: 'octopus', kind: -1, phase: 0, at: s.at, width, height, right: s.right, shown: own, sprite, school: -1, tlMm: (width / this.reef.drawnTimes) * 1000 });
      const pose = this.poses.get(id);
      const b = pose ? Math.min(1, Math.max(0, pose.blend)) : 0;
      const size = pose?.scale ?? 1;
      const alpha = own * (pose?.alpha ?? 1);
      const at = {
        x: s.at.x + ((pose?.at.x ?? s.at.x) - s.at.x) * b, y: s.at.y + ((pose?.at.y ?? s.at.y) - s.at.y) * b, z: s.at.z + ((pose?.at.z ?? s.at.z) - s.at.z) * b,
        width: width * size * (pose?.scaleX ?? 1), height: height * size, right: pose?.right !== undefined && b > 0.5 ? pose.right : s.right,
        ...(pose?.flash ? { flash: pose.flash } : {}),
      };
      // The color it leaves drawn whole under the one it fades to (one shape on the same cells, `scripts/octopusArt.ts`),
      // so the fade changes its colors, never how much of it is seen; the two at one place keep their order.
      if (s.fade < 1) draws.push({ ...at, rect: this.rects.get(s.from) as [number, number, number, number], alpha });
      draws.push({ ...at, rect: this.rects.get(s.to) as [number, number, number, number], alpha: s.fade < 1 ? alpha * s.fade : alpha });
    }
    this.lastPosable = posable;
    this.batch.set(draws);
  }

  /** The octopuses as last drawn, for a hunt (M252). */
  posable(): readonly PosableFish[] {
    return this.lastPosable;
  }

  /** An octopus drawn by a hunt's pose from now (M252), or given back to its own (null). */
  pose(id: string, pose: FishPose | null): void {
    if (pose) this.poses.set(id, pose);
    else this.poses.delete(id);
  }

  /** The octopuses as drawn now. */
  drawnNow(): OctopusDrawn[] {
    return [...this.shown].flatMap(([key, s]) => {
      if (!s.drawn) return [];
      const forage = foragesOn(s.octopus.key, this.epochDay, this.lightHours).find(([a, b]) => this.hour >= a && this.hour < b);
      // Its color the one it shows the more, the one it turns to named only while it shows the less (M158's review: the
      // card read "mottled, turning mottled" through each fade's second half).
      return [{ key, octopus: s.octopus, ...s.drawn, alpha: Math.max(0, s.alpha), color: s.fade < 0.5 ? s.from : s.to, fadingTo: s.fade < 0.5 ? s.to : null, backAt: forage?.[1] ?? this.hour }];
    });
  }

  /** The octopus under a point on the screen, by its key, and how far the tap is from its foot on the screen, px (as a fish's is from its sprite's foot, `FishSchools.pickNear`, so the two are weighed alike: M158's second review); null for none. */
  pick(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): { key: number; px: number; away: number } | null {
    const d = pickNearest(this.drawnNow(), camera, x, y, widthPx, heightPx, withinPx);
    if (!d) return null;
    const at = d.middle.clone().setY(d.middle.y - d.height / 2).project(camera);
    return { key: d.key, px: Math.hypot(((at.x - x) * widthPx) / 2, ((at.y - y) * heightPx) / 2), away: d.middle.distanceTo(camera.position) };
  }

  /** An octopus by its key as drawn now, or null where it is in its den. */
  drawnOf(key: number): OctopusDrawn | null {
    return this.drawnNow().find((d) => d.key === key) ?? null;
  }

  /**
   * Where a tag's mark stands over an octopus of an island (M156's marks): over one octopus and no other, the one it was
   * tagged from (`remembered`, this session's) where it is in the hectare that day, by its island alone (M201's second
   * review: a young one tagged grew up out of its youth's and lost its mark); else one of its island and youth (its
   * island's where none of its youth is drawn) by its tag's own draw (`tag`), so another tag's death or a new one moves it
   * not (M201's second review: by its rank among the tags, a death moved it), the same all day; over it while it is
   * drawn, fading out where it stands as its forage ends included (M201's third review: at half seen it left for the den,
   * up to `rangeM` away), over its den while it is in it (M201, the user's report: the mark over the first of its island
   * drawn went to the one beside it, then back and forth as each went out and home); null for none.
   */
  markOf(island: number, young: boolean, remembered: number | null = null, tag = 0): THREE.Vector3 | null {
    const isle = this.day.filter((o) => o.island === island);
    const own = isle.filter((o) => o.young === young);
    const pool = (own.length > 0 ? own : isle).map((o) => o.key).sort((a, b) => a - b);
    const key = remembered !== null && isle.some((o) => o.key === remembered) ? remembered : pool[Math.floor(hash3(tag, 0, 201) * pool.length)];
    if (key === undefined) return null;
    const drawn = this.drawnNow().find((d) => d.key === key && d.alpha > 0);
    if (drawn) return drawn.middle.clone();
    const den = this.dens.get(key);
    return den ? den.clone().setY(den.y + OCTOPUS_DRAW.denMarkM) : null;
  }

  /** How many are out now. */
  outNow(): number {
    return [...this.shown.values()].filter((s) => s.wanted).length;
  }

  fogUniforms(): ReturnType<FishBatch['fogUniforms']> {
    return this.batch.fogUniforms();
  }

  /** Lit as the scene's animals are, at least by `leastLight` (its brightest channel; the light's color kept), M201. */
  setLight(r: number, g: number, b: number): void {
    const up = Math.max(1, OCTOPUS_DRAW.leastLight / Math.max(r, g, b, 1e-6));
    this.batch.setLight(r * up, g * up, b * up);
  }

  /** What the batch draws, for the tests. */
  draws(): FishDraw[] {
    return this.batch.drawn();
  }

  dispose(): void {
    this.batch.dispose();
  }
}
